import React, { ReactNode, useEffect, useRef, useState, useCallback } from 'react';
import {
  View,
  ViewStyle,
  StyleSheet,
  Platform,
} from 'react-native';
import { useTutorial } from '../../../context/TutorialProvider';
import { useRoute } from '@react-navigation/native';

interface Props {
  children: ReactNode;
  order: number;
  text: string;
  contextName: string;
  onPress?: () => void;
  topInset?: number;
  customStyle?: ViewStyle;
  additionalWidth?: number;
  additionalHeight?: number;
  customY?: number;
  customX?: number;
  screen?: string;
  scrollRef?: React.RefObject<any>;
  skipOnBackpress?: boolean;
}

export const Step = ({
  children,
  order,
  text,
  contextName,
  additionalWidth,
  additionalHeight,
  customX,
  customY,
  onPress,
  topInset,
  customStyle,
  screen,
  skipOnBackpress,
}: Props) => {
  const { registerStep, isActive, currentStep, steps, activeContext } = useTutorial();
  const route = useRoute();
  const ref = useRef<View>(null);
  const currentScreen = route.name;

  const [isBlockingClicks, setIsBlockingClicks] = useState(false);
  const lastPosition = useRef<{x: number; y: number; width: number; height: number} | null>(null);
  const measureAttempts = useRef(0);
  const maxAttempts = 5;
  
  // Registrar sempre que o tutorial estiver ativo para este contexto
  const shouldRun = isActive && activeContext === contextName;
  const isIOS = Platform.OS === 'ios';

  const registerStepData = useCallback((
    x: number,
    y: number,
    width: number,
    height: number,
  ) => {
    if ([x, y, width, height].some(v => v == null || Number.isNaN(v))) return;
    // Ignorar posições claramente erradas (fora da tela ou negativas)
    if (x < 0 || y < 0 || width <= 0 || height <= 0) return;

    const adjustedY = topInset ? y + topInset : y;

    const payload = {
      order,
      text,
      position: { x, y: adjustedY, width, height },
      onPress,
      additionalWidth,
      additionalHeight,
      customX,
      customY,
      screen: screen ?? (currentScreen as string),
      params: route.params,
      skipOnBackpress,
      contextName,
    };

    registerStep(payload);
  }, [order, text, onPress, additionalWidth, additionalHeight, customX, customY, screen, currentScreen, route.params, skipOnBackpress, contextName, topInset, registerStep]);

  const measureAndRegister = useCallback(() => {
    ref.current?.measureInWindow((x, y, width, height) => {
      if ([x, y, width, height].some(v => v == null || Number.isNaN(v))) return;
      
      const last = lastPosition.current;
      const positionChanged = !last || 
        Math.abs(last.x - x) > 2 || 
        Math.abs(last.y - y) > 2;
      
      lastPosition.current = {x, y, width, height};
      
      // Sempre registra se a posição mudou ou é a primeira medição
      if (positionChanged || measureAttempts.current === 0) {
        registerStepData(x, y, width, height);
      }
    });
  }, [registerStepData]);

  // Medir quando shouldRun mudar
  useEffect(() => {
    if (!shouldRun) {
      measureAttempts.current = 0;
      lastPosition.current = null;
      return;
    }

    if (isIOS) {
      setIsBlockingClicks(true);
    }

    // Delay inicial de 500ms para dar tempo da tela renderizar
    const initialDelay = setTimeout(() => {
      // Medir após o delay
      measureAndRegister();
      measureAttempts.current = 1;

      // Re-medir algumas vezes para pegar a posição estabilizada
      const intervals = [100, 250, 400];
      intervals.forEach((delay, i) => {
        setTimeout(() => {
          measureAttempts.current = i + 2;
          measureAndRegister();
          
          // Liberar clicks após última medição
          if (i === intervals.length - 1 && isIOS) {
            setIsBlockingClicks(false);
          }
        }, delay);
      });
    }, 500);

    return () => {
      clearTimeout(initialDelay);
      if (isIOS) {
        setIsBlockingClicks(false);
      }
    };
  }, [shouldRun, measureAndRegister, isIOS]);

  // Re-medir no layout change
  const handleLayout = useCallback(() => {
    if (shouldRun) {
      measureAndRegister();
    }
  }, [shouldRun, measureAndRegister]);

  return (
    <>
      {isIOS && isBlockingClicks && <View style={styles.blockingLayer} />}
      <View
        ref={ref}
        style={customStyle}
        onLayout={handleLayout}
      >
        {children}
      </View>
    </>
  );
};

const styles = StyleSheet.create({
  blockingLayer: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: 'rgba(0, 0, 0, 0)',
    zIndex: 10,
  },
});